/**
 * 高性能运行时类型检查工具
 *
 * @description
 * 提供全面的运行时类型检查和类型守卫功能，替代不安全的类型断言。
 * 支持基础类型、复合类型、自定义验证器等。
 *
 * @example
 * ```typescript
 * // 基础类型检查
 * if (TypeGuards.isString(value)) {
 *   // value 现在是 string 类型
 *   console.log(value.toUpperCase());
 * }
 *
 * // 复合类型检查
 * const result = TypeGuards.checkObject(data, {
 *   name: TypeGuards.validators.string,
 *   age: TypeGuards.validators.number,
 *   email: TypeGuards.validators.optional(TypeGuards.validators.string)
 * });
 *
 * // 数组类型检查
 * if (TypeGuards.isArrayOf(value, TypeGuards.isNumber)) {
 *   // value 现在是 number[] 类型
 *   const sum = value.reduce((a, b) => a + b, 0);
 * }
 *
 * // 自定义验证器
 * const isPositiveNumber = TypeGuards.createValidator<number>(
 *   (value): value is number => typeof value === 'number' && value > 0,
 *   'PositiveNumber'
 * );
 * ```
 */
export class TypeGuards {
    // ===== 基础类型守卫 =====
    /**
     * 检查是否为字符串
     */
    static isString(value) {
        return typeof value === 'string';
    }
    /**
     * 检查是否为数字
     */
    static isNumber(value) {
        return typeof value === 'number' && !isNaN(value);
    }
    /**
     * 检查是否为布尔值
     */
    static isBoolean(value) {
        return typeof value === 'boolean';
    }
    /**
     * 检查是否为函数
     */
    static isFunction(value) {
        return typeof value === 'function';
    }
    /**
     * 检查是否为对象（非null）
     */
    static isObject(value) {
        return typeof value === 'object' && value !== null;
    }
    /**
     * 检查是否为数组
     */
    static isArray(value) {
        return Array.isArray(value);
    }
    /**
     * 检查是否为null或undefined
     */
    static isNullish(value) {
        return value == null;
    }
    /**
     * 检查是否不为null或undefined
     */
    static isNotNull(value) {
        return value != null;
    }
    // ===== 复合类型守卫 =====
    /**
     * 检查是否为指定类型的数组
     */
    static isArrayOf(value, itemGuard) {
        return Array.isArray(value) && value.every(itemGuard);
    }
    /**
     * 检查是否为字符串数组
     */
    static isStringArray(value) {
        return this.isArrayOf(value, this.isString);
    }
    /**
     * 检查是否为数字数组
     */
    static isNumberArray(value) {
        return this.isArrayOf(value, this.isNumber);
    }
    /**
     * 检查是否为指定类的实例
     */
    static isInstanceOf(value, constructor) {
        return value instanceof constructor;
    }
    /**
     * 检查对象是否具有指定的属性
     */
    static hasProperty(value, property) {
        return this.isObject(value) && property in value;
    }
    /**
     * 检查对象是否具有指定类型的属性
     */
    static hasPropertyOfType(value, property, typeGuard) {
        return this.hasProperty(value, property) &&
            typeGuard(value[property]);
    }
    // ===== 高级类型检查 =====
    /**
     * 创建自定义验证器
     */
    static createValidator(guard, typeName, errorMessage) {
        return {
            validate: guard,
            typeName,
            errorMessage: errorMessage || `Expected ${typeName}`
        };
    }
    /**
     * 检查对象结构
     */
    static checkObject(value, schema) {
        if (!this.isObject(value)) {
            return {
                success: false,
                value: value,
                error: 'Value is not an object',
                expectedType: 'object',
                actualType: typeof value
            };
        }
        const obj = value;
        const result = {};
        for (const [key, validator] of Object.entries(schema)) {
            const propValue = obj[key];
            if (!validator.validate(propValue)) {
                return {
                    success: false,
                    value: value,
                    error: `Property '${key}' ${validator.errorMessage || `is not of type ${validator.typeName}`}`,
                    expectedType: validator.typeName,
                    actualType: typeof propValue
                };
            }
            result[key] = propValue;
        }
        return {
            success: true,
            value: result
        };
    }
    /**
     * 安全类型转换
     */
    static safeCast(value, validator) {
        if (validator.validate(value)) {
            return {
                success: true,
                value
            };
        }
        return {
            success: false,
            value: value,
            error: validator.errorMessage || `Value is not of type ${validator.typeName}`,
            expectedType: validator.typeName,
            actualType: typeof value
        };
    }
    /**
     * 断言类型（开发模式下抛出错误）
     */
    static assertType(value, validator, message) {
        if (!validator.validate(value)) {
            const error = message ||
                validator.errorMessage ||
                `Type assertion failed: expected ${validator.typeName}, got ${typeof value}`;
            throw new TypeError(error);
        }
    }
    /**
     * 尝试类型转换
     */
    static tryConvert(value, converter, validator) {
        try {
            const converted = converter(value);
            if (validator.validate(converted)) {
                return {
                    success: true,
                    value: converted
                };
            }
            return {
                success: false,
                value: converted,
                error: `Conversion result is not of type ${validator.typeName}`,
                expectedType: validator.typeName,
                actualType: typeof converted
            };
        }
        catch (error) {
            return {
                success: false,
                value: value,
                error: `Conversion failed: ${error instanceof Error ? error.message : String(error)}`,
                expectedType: validator.typeName,
                actualType: typeof value
            };
        }
    }
    // ===== 常用转换器 =====
    /**
     * 字符串转数字
     */
    static stringToNumber(value) {
        return this.tryConvert(value, (v) => {
            if (typeof v === 'string') {
                const num = Number(v);
                if (isNaN(num)) {
                    throw new Error('Invalid number format');
                }
                return num;
            }
            throw new Error('Value is not a string');
        }, this.validators.number);
    }
    /**
     * 任意值转字符串
     */
    static toString(value) {
        return this.tryConvert(value, (v) => String(v), this.validators.string);
    }
    /**
     * 任意值转布尔值
     */
    static toBoolean(value) {
        return this.tryConvert(value, (v) => Boolean(v), this.validators.boolean);
    }
    // ===== 范围检查 =====
    /**
     * 检查数字是否在指定范围内
     */
    static isInRange(value, min, max, inclusive = true) {
        if (!this.isNumber(value)) {
            return false;
        }
        return inclusive ?
            (value >= min && value <= max) :
            (value > min && value < max);
    }
    /**
     * 检查字符串长度是否在指定范围内
     */
    static isStringLengthInRange(value, minLength, maxLength) {
        return this.isString(value) &&
            value.length >= minLength &&
            value.length <= maxLength;
    }
    /**
     * 检查数组长度是否在指定范围内
     */
    static isArrayLengthInRange(value, minLength, maxLength) {
        return this.isArray(value) &&
            value.length >= minLength &&
            value.length <= maxLength;
    }
    // ===== 模式匹配 =====
    /**
     * 检查字符串是否匹配正则表达式
     */
    static matchesPattern(value, pattern) {
        return this.isString(value) && pattern.test(value);
    }
    /**
     * 检查是否为有效的电子邮件地址
     */
    static isEmail(value) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return this.matchesPattern(value, emailPattern);
    }
    /**
     * 检查是否为有效的URL
     */
    static isUrl(value) {
        if (!this.isString(value)) {
            return false;
        }
        try {
            new URL(value);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * 检查是否为有效的UUID
     */
    static isUuid(value) {
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return this.matchesPattern(value, uuidPattern);
    }
}
/** 内置验证器 */
TypeGuards.validators = {
    /** 字符串验证器 */
    string: TypeGuards.createValidator((value) => typeof value === 'string', 'string'),
    /** 数字验证器 */
    number: TypeGuards.createValidator((value) => typeof value === 'number' && !isNaN(value), 'number'),
    /** 布尔值验证器 */
    boolean: TypeGuards.createValidator((value) => typeof value === 'boolean', 'boolean'),
    /** 函数验证器 */
    function: TypeGuards.createValidator((value) => typeof value === 'function', 'function'),
    /** 对象验证器 */
    object: TypeGuards.createValidator((value) => typeof value === 'object' && value !== null, 'object'),
    /** 数组验证器 */
    array: TypeGuards.createValidator((value) => Array.isArray(value), 'array'),
    /** 非空验证器 */
    notNull: TypeGuards.createValidator((value) => value != null, 'not null'),
    /** 整数验证器 */
    integer: TypeGuards.createValidator((value) => typeof value === 'number' && Number.isInteger(value), 'integer'),
    /** 正数验证器 */
    positiveNumber: TypeGuards.createValidator((value) => typeof value === 'number' && value > 0, 'positive number'),
    /** 非负数验证器 */
    nonNegativeNumber: TypeGuards.createValidator((value) => typeof value === 'number' && value >= 0, 'non-negative number'),
    /** 非空字符串验证器 */
    nonEmptyString: TypeGuards.createValidator((value) => typeof value === 'string' && value.trim().length > 0, 'non-empty string'),
    /** 可选验证器工厂 */
    optional: (validator) => ({
        validate: (value) => value === undefined || validator.validate(value),
        typeName: `${validator.typeName} | undefined`,
        errorMessage: `Expected ${validator.typeName} or undefined`
    }),
    /** 可空验证器工厂 */
    nullable: (validator) => ({
        validate: (value) => value === null || validator.validate(value),
        typeName: `${validator.typeName} | null`,
        errorMessage: `Expected ${validator.typeName} or null`
    }),
    /** 联合类型验证器工厂 */
    union: (...validators) => ({
        validate: (value) => validators.some(v => v.validate(value)),
        typeName: validators.map(v => v.typeName).join(' | '),
        errorMessage: `Expected one of: ${validators.map(v => v.typeName).join(', ')}`
    })
};
//# sourceMappingURL=TypeGuards.js.map